import React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { GripVertical, Trash2, Star, ExternalLink, Shuffle, CheckCircle2, Circle, Pencil, ChevronsUp, ChevronsDown } from 'lucide-react';

import { useLongPress } from '../hooks/useLongPress';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';

// Control "Mover a posición #": el número de orden es pulsable y abre un popover
// con un campo numérico (salto directo a cualquier posición global) + atajos
// "Al principio" / "Al final". Resuelve mover canciones lejos (ej. #4 → #50)
// sin tener que arrastrar a través del scroll. Solo en preparación (no en vivo).
const MovePositionControl = ({ index, total, onMove }) => {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(String(index + 1));

  React.useEffect(() => {
    if (open) setValue(String(index + 1));
  }, [open, index]);

  const commit = (targetPos) => {
    const n = parseInt(targetPos, 10);
    if (!Number.isNaN(n)) {
      const clamped = Math.max(1, Math.min(total, n));
      onMove(clamped - 1);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="text-slate-400 hover:text-cyan-300 text-xs sm:text-sm flex-shrink-0 w-6 sm:w-7 text-left tabular-nums underline decoration-dotted decoration-slate-600 underline-offset-2 cursor-pointer"
          title="Mover a otra posición"
          data-testid={`move-position-trigger-${index}`}
        >
          {index + 1}.
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-56 bg-slate-800 border-slate-700 text-white p-3"
        data-testid="move-position-popover"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <p className="text-xs text-slate-300 mb-2 font-medium">Mover a posición</p>
        <div className="flex items-center gap-2 mb-2">
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={total}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit(value);
              }
            }}
            autoFocus
            className="w-16 px-2 py-1 rounded bg-slate-900 border border-slate-600 text-white text-sm focus:outline-none focus:border-cyan-500"
            data-testid="move-position-input"
          />
          <span className="text-slate-500 text-xs whitespace-nowrap">de {total}</span>
          <button
            type="button"
            onClick={() => commit(value)}
            className="ml-auto px-3 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium shrink-0"
            data-testid="move-position-confirm"
          >
            Mover
          </button>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => commit(1)}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs"
            data-testid="move-position-top"
          >
            <ChevronsUp className="w-3 h-3" /> Al principio
          </button>
          <button
            type="button"
            onClick={() => commit(total)}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs"
            data-testid="move-position-bottom"
          >
            <ChevronsDown className="w-3 h-3" /> Al final
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const openExternalLink = (url, event) => {
  // Prevenir comportamiento por defecto
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  
  // Detectar si estamos en móvil
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // En móvil: cambiar la ubicación actual en lugar de abrir nueva pestaña
    window.location.href = url;
  } else {
    // En desktop: abrir en nueva pestaña
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

// Componente individual de canción arrastrable con información completa
const SongItem = ({ song, index, displayIndex, onRemove, showDetails = false, onMixSuggestions, isPlayed = false, onTogglePlayed, isLiveMode = false, onEditSong, onMoveTo, total, dragDisabled = false }) => {
  const editLongPress = useLongPress(
    () => onEditSong && onEditSong(song),
    { delay: 600 }
  );
  return (
    <Draggable draggableId={String(song.id)} index={index} isDragDisabled={dragDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`flex items-center gap-1 sm:gap-2 p-1.5 sm:p-3 rounded transition-opacity ${
            song._inicio_hora_loca
              ? 'bg-pink-950/40 border border-pink-500/50 ring-1 ring-pink-500/30'
              : song._imprescindible
              ? 'bg-amber-950/30 border border-amber-500/40 ring-1 ring-amber-500/20'
              : song._de_spotify_cliente
              ? 'bg-green-950/30 border border-green-500/30'
              : 'bg-slate-900/50 border border-slate-700'
          } ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-cyan-500 opacity-90' : ''
          } ${isPlayed ? 'opacity-50 bg-slate-900/30' : ''} ${isLiveMode ? 'sm:p-4' : ''}`}
          data-testid={`custom-list-song-${song.id}`}
          data-played={isPlayed ? 'true' : 'false'}
          data-imperdible={song._imprescindible ? 'true' : 'false'}
          data-inicio-hora-loca={song._inicio_hora_loca ? 'true' : 'false'}
        >
          {/* Drag handle — atenuado cuando hay un filtro activo (arrastrar un
              subconjunto filtrado es ambiguo; en ese caso se usa "Mover a posición #") */}
          <div
            {...provided.dragHandleProps}
            className={`p-0.5 sm:p-1 flex-shrink-0 ${
              dragDisabled
                ? 'text-slate-700 cursor-not-allowed'
                : 'cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300'
            }`}
            title={dragDisabled ? 'Quita el filtro para arrastrar (usa el nº para mover)' : 'Arrastra para reordenar'}
          >
            <GripVertical className="w-3 h-3 sm:w-4 sm:h-4" />
          </div>

          {/* Index number — muestra la posición REAL global; pulsable para
              "Mover a posición #" (solo en preparación) */}
          {displayIndex !== undefined && (
            onMoveTo ? (
              <MovePositionControl index={displayIndex} total={total} onMove={onMoveTo} />
            ) : (
              <span className="text-slate-500 text-xs sm:text-sm flex-shrink-0 w-5 sm:w-6">{displayIndex + 1}.</span>
            )
          )}

          {/* Song info - Compacto en una columna */}
          <div className="flex-1 min-w-0">
            <div
              className={`flex items-baseline gap-1 flex-wrap select-none transition-transform ${
                onEditSong ? 'cursor-pointer touch-manipulation' : ''
              } ${editLongPress.isPressing ? 'scale-[0.97] opacity-80' : ''}`}
              {...(onEditSong ? editLongPress.bindings : {})}
              title={onEditSong ? 'Mantén pulsado 0,6s para editar la canción' : undefined}
              data-testid={`song-title-area-${song.id}`}
            >
              {/* Iter89.4: Badges de marcadores especiales */}
              {song._inicio_hora_loca && (
                <span
                  className="px-1.5 py-0.5 bg-pink-600/30 text-pink-300 border border-pink-500/40 rounded text-[10px] font-bold flex-shrink-0"
                  title="Canción de inicio de Hora Loca"
                  data-testid={`badge-inicio-hora-loca-${song.id}`}
                >
                  🎉 INICIO HL
                </span>
              )}
              {song._imprescindible && !song._inicio_hora_loca && (
                <span
                  className="px-1.5 py-0.5 bg-amber-500/25 text-amber-300 border border-amber-500/40 rounded text-[10px] font-bold flex-shrink-0"
                  title="Canción imperdible (cliente la pidió)"
                  data-testid={`badge-imperdible-${song.id}`}
                >
                  ⭐ IMPERDIBLE
                </span>
              )}
              {song._de_spotify_cliente && !song._imprescindible && !song._inicio_hora_loca && (
                <span
                  className="px-1.5 py-0.5 bg-green-600/25 text-green-300 border border-green-500/40 rounded text-[10px] font-bold flex-shrink-0"
                  title="Canción importada de la playlist Spotify del cliente"
                  data-testid={`badge-spotify-cliente-${song.id}`}
                >
                  🎵 SPOTIFY CLIENTE
                </span>
              )}
              <p className={`text-white font-medium text-xs sm:text-sm truncate flex-shrink ${isPlayed ? 'line-through text-slate-400' : ''}`}>
                {song.titulo || song.title || 'Sin título'}
              </p>
              {onEditSong && (
                <Pencil
                  className="w-3 h-3 text-slate-500 hover:text-cyan-400 transition-colors flex-shrink-0 hidden sm:inline-block"
                  aria-hidden="true"
                />
              )}
              <span className="text-slate-500 text-xs flex-shrink-0">•</span>
              <p className={`text-slate-400 text-xs truncate flex-shrink ${isPlayed ? 'line-through' : ''}`}>
                {song.artista || song.artist || 'Artista desconocido'}
              </p>
            </div>

            {/* Details: Comprimidos en UNA sola línea con iconos/badges más pequeños */}
            {showDetails && (
              <div className="flex items-center gap-1 mt-0.5 overflow-x-auto scrollbar-hide">
                {song.rating > 0 && (
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {[...Array(song.rating)].map((_, i) => (
                      <Star key={i} className="w-2 h-2 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                )}
                {song.favorito && (
                  <span className="text-xs flex-shrink-0">❤️</span>
                )}
                {song.genres && song.genres.slice(0, 1).map((genre) => (
                  <span key={genre} className="px-1 py-0.5 bg-cyan-600/30 text-cyan-300 rounded text-[10px] sm:text-xs flex-shrink-0">
                    {genre}
                  </span>
                ))}
                {song.bpm && (
                  <span className="px-1 py-0.5 bg-slate-700 text-slate-300 rounded text-[10px] sm:text-xs flex-shrink-0">
                    {song.bpm}
                  </span>
                )}
                {song.energia && (
                  <span className={`px-1 py-0.5 rounded text-[10px] sm:text-xs flex-shrink-0 ${
                    song.energia === 'Muy Alta' ? 'bg-red-900/30 text-red-400' :
                    song.energia === 'Alta' ? 'bg-orange-900/30 text-orange-400' :
                    song.energia === 'Media' ? 'bg-yellow-900/30 text-yellow-400' :
                    'bg-blue-900/30 text-blue-400'
                  }`}>
                    {song.energia === 'Muy Alta' ? '⚡⚡⚡⚡' :
                     song.energia === 'Alta' ? '⚡⚡⚡' :
                     song.energia === 'Media' ? '⚡⚡' : '⚡'}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Action buttons - Altura completa del contenedor */}
          <div className="flex gap-0.5 sm:gap-1 flex-shrink-0 self-stretch">
            {/* Played toggle button (marca canción como reproducida) */}
            {onTogglePlayed && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePlayed(song.id);
                }}
                className={`${isLiveMode ? 'px-3 sm:px-4 min-w-[44px]' : 'px-2 sm:px-3'} flex items-center justify-center rounded transition-colors ${
                  isPlayed
                    ? 'bg-emerald-600/40 hover:bg-emerald-600/60'
                    : 'bg-slate-700/40 hover:bg-emerald-600/30'
                }`}
                title={isPlayed ? 'Desmarcar como reproducida' : 'Marcar como reproducida'}
                data-testid={`toggle-played-btn-${song.id}`}
                aria-pressed={isPlayed}
              >
                {isPlayed ? (
                  <CheckCircle2 className={`${isLiveMode ? 'w-5 h-5 sm:w-6 sm:h-6' : 'w-4 h-4 sm:w-5 sm:h-5'} text-emerald-300`} />
                ) : (
                  <Circle className={`${isLiveMode ? 'w-5 h-5 sm:w-6 sm:h-6' : 'w-4 h-4 sm:w-5 sm:h-5'} text-slate-400`} />
                )}
              </button>
            )}
            {/* Mix suggestions button */}
            {onMixSuggestions && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMixSuggestions(song);
                }}
                className="px-2 sm:px-3 flex items-center justify-center rounded bg-purple-600/20 hover:bg-purple-600/40 transition-colors"
                title="Sugerencias de mezcla"
                data-testid={`mix-suggestions-btn-${song.id}`}
              >
                <Shuffle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
              </button>
            )}
            {song.spotify_url && (
              <button
                onClick={(e) => openExternalLink(song.spotify_url, e)}
                className="px-2 sm:px-3 flex items-center justify-center rounded bg-green-600/20 hover:bg-green-600/40 transition-colors"
                title="Abrir en Spotify"
              >
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              </button>
            )}
            {song.spotify_uri && (
              <button
                onClick={() => {
                  if (window.addToSpotifyQueue) {
                    window.addToSpotifyQueue(song.spotify_uri, song.title, song.artist);
                  }
                }}
                className="px-2 sm:px-3 flex items-center justify-center rounded bg-purple-600/20 hover:bg-purple-600/40 transition-colors"
                title="Añadir a cola de Spotify"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </button>
            )}
            {onRemove && !isLiveMode && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(song.id);
                }}
                className="px-2 sm:px-3 flex items-center justify-center rounded bg-red-600/20 hover:bg-red-600/40 transition-colors"
                title="Eliminar"
              >
                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 text-red-400" />
              </button>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

// Componente principal de lista reordenable
export const SortableSongList = ({ songs, onReorder, onRemove, showDetails = false, showNumbers = false, onMixSuggestions, playedState = {}, onTogglePlayed, isLiveMode = false, onEditSong, orderedIds, onMoveToPosition, dragDisabled = false }) => {

  // Posición REAL (global) de cada canción dentro de la lista completa.
  // Cuando hay un filtro/búsqueda activo, `songs` es un subconjunto: usamos
  // `orderedIds` (orden canónico completo) para mostrar el nº verdadero y para
  // que "Mover a posición #" salte a la posición global correcta.
  const hasFullOrder = Array.isArray(orderedIds) && orderedIds.length > 0;
  const totalCount = hasFullOrder ? orderedIds.length : songs.length;
  const realIndexOf = (song, fallback) => {
    if (hasFullOrder) {
      const i = orderedIds.indexOf(song.id);
      if (i !== -1) return i;
    }
    return fallback;
  };

  const handleDragStart = (start) => {
    console.log('🎯 Drag started:', start.draggableId);
  };
  
  const handleDragUpdate = (update) => {
    console.log('🔄 Drag update:', update.destination?.index);
  };
  
  const handleDragEnd = (result) => {
    // Seguridad: con filtro activo el drag está deshabilitado (reordenar un
    // subconjunto filtrado borraría el resto de la lista).
    if (dragDisabled) return;
    console.log('🏁 Drag end:', { 
      source: result.source?.index, 
      destination: result.destination?.index,
      reason: result.reason 
    });
    
    if (!result.destination) {
      console.log('⚠️ Drop outside list or cancelled');
      return;
    }

    if (result.source.index === result.destination.index) {
      console.log('ℹ️ Same position, no reorder');
      return;
    }

    const items = Array.from(songs);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    console.log('✅ Reordered successfully:', { 
      from: result.source.index, 
      to: result.destination.index,
      song: reorderedItem.title 
    });
    
    onReorder(items);
  };

  // Mover una canción a una posición concreta (salto directo, sin arrastrar).
  // Fallback local cuando no hay handler global (onMoveToPosition).
  const moveItem = (fromIndex, toIndex) => {
    const clamped = Math.max(0, Math.min(songs.length - 1, toIndex));
    if (fromIndex === clamped) return;
    const items = Array.from(songs);
    const [moved] = items.splice(fromIndex, 1);
    items.splice(clamped, 0, moved);
    onReorder(items);
  };

  if (songs.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <p>No hay canciones seleccionadas</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <p className="text-slate-400 text-sm">
          {songs.length} {songs.length === 1 ? 'canción' : 'canciones'}
        </p>
        {!isLiveMode && (
          <p className="text-slate-500 text-[11px] sm:text-xs flex items-center gap-1">
            <GripVertical className="w-3 h-3 inline shrink-0" />
            {dragDisabled ? (
              <span>Filtro activo · toca el <span className="text-cyan-400">nº</span> para mover</span>
            ) : (
              <span>Arrastra, o toca el <span className="text-cyan-400">nº</span> para mover lejos</span>
            )}
          </p>
        )}
      </div>

      <DragDropContext 
        onDragStart={handleDragStart}
        onDragUpdate={handleDragUpdate}
        onDragEnd={handleDragEnd}
      >
        <Droppable droppableId="songs-list">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`max-h-[70vh] sm:max-h-[500px] lg:max-h-96 overflow-y-auto pr-1 sm:pr-2 space-y-1 sm:space-y-2 ${
                snapshot.isDraggingOver ? 'bg-cyan-900/10' : ''
              }`}
            >
              {songs.map((song, index) => (
                <SongItem
                  key={song.id}
                  song={song}
                  index={index}
                  displayIndex={showNumbers ? realIndexOf(song, index) : undefined}
                  onRemove={onRemove}
                  showDetails={showDetails}
                  onMixSuggestions={onMixSuggestions}
                  isPlayed={!!playedState[song.id]?.played}
                  onTogglePlayed={onTogglePlayed}
                  isLiveMode={isLiveMode}
                  onEditSong={!isLiveMode ? onEditSong : undefined}
                  dragDisabled={dragDisabled}
                  onMoveTo={(!isLiveMode && showNumbers) ? ((targetGlobalIndex) => {
                    if (onMoveToPosition) onMoveToPosition(song.id, targetGlobalIndex);
                    else moveItem(index, targetGlobalIndex);
                  }) : undefined}
                  total={totalCount}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default SortableSongList;
